/**
 * Brokers Page - Neural Broker Network Connection Hub
 * Version: 6.2.0
 */
import { useState, useEffect } from 'react'
import './Brokers.css'
import EmptyState from '../components/EmptyState'
import SearchInput from '../components/SearchInput'
import FilterDropdown from '../components/FilterDropdown'
import PageHeader from '../components/PageHeader'
import AnimateOnScroll from '../components/AnimateOnScroll'
import IntegrationsList from '../components/IntegrationsList'
import APIKeyManager from '../components/APIKeyManager'

// Logo mapping - maps broker IDs to their logo paths
const BROKER_LOGOS: Record<string, string> = {
  zerodha: '/logos/brokers/zerodha.svg',
  angelone: '/logos/brokers/angelone.svg',
  upstox: '/logos/brokers/upstox.svg',
  fyers: '/logos/brokers/fyers.svg',
  icici: '/logos/brokers/icici.svg',
  hdfc: '/logos/brokers/hdfc.svg',
  kotak: '/logos/brokers/kotak.svg',
  '5paisa': '/logos/brokers/fivepaisa.svg',
  'zerodha-adv': '/logos/brokers/zerodhaadv.svg',
  'zerodha-full': '/logos/brokers/zerodhafull.svg',
  'angelone-adv': '/logos/brokers/angeloneadv.svg',
  'angelone-full': '/logos/brokers/angelonefull.svg',
  'upstox-adv': '/logos/brokers/upstoxadv.svg',
  'upstox-full': '/logos/brokers/upstoxfull.svg',
  'fyers-adv': '/logos/brokers/fyersadv.svg',
  'fyers-full': '/logos/brokers/fyersfull.svg',
  '5paisa-pro': '/logos/brokers/5paisapro.svg',
  'kotak-sec': '/logos/brokers/kotaksec.svg',
  'hdfc-sec': '/logos/brokers/hdfcsec.svg',
  'icici-direct': '/logos/brokers/icicidirect.svg',
  'axis-direct': '/logos/brokers/axisdirect.svg',
  'edelweiss-full': '/logos/brokers/edelweissfull.svg',
  'iifl-sec': '/logos/brokers/iiflsecurities.svg',
  'iifl-wealth': '/logos/brokers/iiflwealth.svg',
  motilal: '/logos/brokers/motilal.svg',
  samco: '/logos/brokers/samco.svg',
  'tradejini-pro': '/logos/brokers/tradejinipro.svg',
  aliceblue: '/logos/brokers/aliceblue.svg',
  dhan: '/logos/brokers/dhan.svg',
  sharekhan: '/logos/brokers/sharekhan.svg',
  iifl: '/logos/brokers/iifl.svg',
  axis: '/logos/brokers/axis.svg',
  edelweiss: '/logos/brokers/edelweiss.svg',
  geojit: '/logos/brokers/geojit.svg',
  groww: '/logos/brokers/groww.svg',
  nuvama: '/logos/brokers/nuvama.svg',
  finvasia: '/logos/brokers/finvasia.svg',
  zebu: '/logos/brokers/zebu.svg',
  prostocks: '/logos/brokers/prostocks.svg',
  mastertrust: '/logos/brokers/mastertrust.svg',
  flattrading: '/logos/brokers/flattrading.svg',
  antweb: '/logos/brokers/antweb.svg',
  bmoney: '/logos/brokers/bmoney.svg',
  kedia: '/logos/brokers/kedia.svg',
  arihant: '/logos/brokers/arihant.svg',
  marwadi: '/logos/brokers/marwadi.svg',
  chasepips: '/logos/brokers/chasepips.svg',
  trustline: '/logos/brokers/trustline.svg',
  rcntrade: '/logos/brokers/rcntrade.svg',
  abclstocks: '/logos/brokers/abclstocks.svg',
  lkp: '/logos/brokers/lkp.svg',
}

// Fallback emoji if logo not found
const BROKER_EMOJI: Record<string, string> = {
  zerodha: '📊',
  angelone: '🧿',
  upstox: '📈',
  fyers: '🎯',
  icici: '🏦',
  hdfc: '💎',
  kotak: '⚡',
  '5paisa': '🌐',
  aliceblue: '🔵',
  dhan: '💹',
  sharekhan: '🦈',
  iifl: '🔷',
  motilal: '📉',
  axis: '🎪',
  edelweiss: '🌟',
  geojit: '🌊',
  groww: '🌱',
  nuvama: '✨',
  finvasia: '💡',
  zebu: '🦎',
  prostocks: '📦',
  mastertrust: '👑',
  flattrading: '🏠',
  antweb: '🐜',
  bmoney: '💵',
  kedia: '📜',
  arihant: '🦁',
  marwadi: '🏛️',
  chasepips: '💱',
  trustline: '✅',
  rcntrade: '📞',
  abclstocks: '📋',
  lkp: '📓',
  tradetron: '🔗',
  'ai-signals': '🤖',
}

// Logo Component
const BrokerLogo: React.FC<{ brokerId: string; name: string; size?: number }> = ({ brokerId, name, size = 40 }) => {
  const logoPath = BROKER_LOGOS[brokerId]
  
  if (logoPath) {
    return (
      <img 
        src={logoPath} 
        alt={`${name} logo`} 
        className="broker-logo"
        style={{ width: size, height: size, objectFit: 'contain' }}
        onError={(e) => {
          // Fallback to emoji on error
          const target = e.target as HTMLImageElement
          target.style.display = 'none'
          const parent = target.parentElement
          if (parent) {
            const emoji = document.createElement('span')
            emoji.className = 'broker-logo-fallback'
            emoji.textContent = BROKER_EMOJI[brokerId] || '🏦'
            emoji.style.fontSize = `${size}px`
            parent.appendChild(emoji)
          }
        }}
      />
    )
  }
  
  return <span className="broker-logo-fallback" style={{ fontSize: size }}>{BROKER_EMOJI[brokerId] || '🏦'}</span>
}

const BROKERS = {
  major: [
    { id: 'zerodha', name: 'Zerodha', status: 'connected', traders: '2.5M+' },
    { id: 'angelone', name: 'Angel One', status: 'connected', traders: '5M+' },
    { id: 'upstox', name: 'Upstox', status: 'connected', traders: '3M+' },
    { id: 'fyers', name: 'Fyers', status: 'connected', traders: '1.5M+' },
    { id: 'icici', name: 'ICICI Direct', status: 'ready', traders: '4M+' },
    { id: 'hdfc', name: 'HDFC Securities', status: 'ready', traders: '3.5M+' },
    { id: 'kotak', name: 'Kotak Securities', status: 'ready', traders: '2M+' },
    { id: '5paisa', name: '5Paisa', status: 'connected', traders: '1M+' },
  ],
  full: [
    { id: 'zerodha-adv', name: 'Zerodha Advanced', status: 'ready', traders: '-' },
    { id: 'zerodha-full', name: 'Zerodha Full', status: 'ready', traders: '-' },
    { id: 'angelone-adv', name: 'Angel One Advanced', status: 'ready', traders: '-' },
    { id: 'angelone-full', name: 'Angel One Full', status: 'ready', traders: '-' },
    { id: 'upstox-adv', name: 'Upstox Advanced', status: 'ready', traders: '-' },
    { id: 'upstox-full', name: 'Upstox Full', status: 'ready', traders: '-' },
    { id: 'fyers-adv', name: 'Fyers Advanced', status: 'ready', traders: '-' },
    { id: 'fyers-full', name: 'Fyers Full', status: 'ready', traders: '-' },
    { id: '5paisa-pro', name: '5Paisa Pro', status: 'ready', traders: '-' },
    { id: 'kotak-sec', name: 'Kotak Securities', status: 'ready', traders: '-' },
    { id: 'hdfc-sec', name: 'HDFC Securities', status: 'ready', traders: '-' },
    { id: 'icici-direct', name: 'ICICI Direct', status: 'ready', traders: '-' },
    { id: 'axis-direct', name: 'Axis Direct', status: 'ready', traders: '-' },
    { id: 'edelweiss-full', name: 'Edelweiss Full', status: 'ready', traders: '-' },
    { id: 'iifl-sec', name: 'IIFL Securities', status: 'ready', traders: '-' },
    { id: 'iifl-wealth', name: 'IIFL Wealth', status: 'ready', traders: '-' },
    { id: 'motilal', name: 'Motilal Oswal', status: 'ready', traders: '-' },
    { id: 'samco', name: 'Samco', status: 'ready', traders: '-' },
    { id: 'tradejini-pro', name: 'Tradejini Pro', status: 'ready', traders: '-' },
  ],
  mid: [
    { id: 'aliceblue', name: 'Alice Blue', status: 'ready', traders: '-' },
    { id: 'dhan', name: 'Dhan', status: 'ready', traders: '-' },
    { id: 'sharekhan', name: 'Sharekhan', status: 'ready', traders: '-' },
    { id: 'iifl', name: 'IIFL', status: 'ready', traders: '-' },
    { id: 'motilal', name: 'Motilal', status: 'ready', traders: '-' },
    { id: 'axis', name: 'Axis', status: 'ready', traders: '-' },
    { id: 'edelweiss', name: 'Edelweiss', status: 'ready', traders: '-' },
    { id: 'geojit', name: 'Geojit', status: 'ready', traders: '-' },
    { id: 'groww', name: 'Groww', status: 'ready', traders: '-' },
    { id: 'nuvama', name: 'Nuvama', status: 'ready', traders: '-' },
    { id: 'finvasia', name: 'Finvasia', status: 'ready', traders: '-' },
    { id: 'zebu', name: 'Zebu', status: 'ready', traders: '-' },
    { id: 'prostocks', name: 'ProStocks', status: 'ready', traders: '-' },
    { id: 'mastertrust', name: 'Master Trust', status: 'ready', traders: '-' },
    { id: 'flattrading', name: 'Flat Trading', status: 'ready', traders: '-' },
  ],
  small: [
    { id: 'antweb', name: 'Antweb', status: 'available', traders: '-' },
    { id: 'bmoney', name: 'BMoney', status: 'available', traders: '-' },
    { id: 'kedia', name: 'Kedia', status: 'available', traders: '-' },
    { id: 'arihant', name: 'Arihant', status: 'available', traders: '-' },
    { id: 'marwadi', name: 'Marwadi', status: 'available', traders: '-' },
    { id: 'chasepips', name: 'Chase Pips', status: 'available', traders: '-' },
    { id: 'trustline', name: 'Trustline', status: 'available', traders: '-' },
    { id: 'rcntrade', name: 'RCN Trade', status: 'available', traders: '-' },
    { id: 'abclstocks', name: 'ABCL Stocks', status: 'available', traders: '-' },
    { id: 'lkp', name: 'LKP', status: 'available', traders: '-' },
  ],
  integrations: [
    { id: 'tradetron', name: 'TradetrON', status: 'ready', traders: '-', url: '/tradetron-settings', description: 'Bi-directional signal integration' },
    { id: 'ai-signals', name: 'AI Signals', status: 'ready', traders: '-', url: '/copilot', description: 'AI-powered trading signals' },
  ]
}

function Brokers() {
  const [selectedCategory, setSelectedCategory] = useState('major')
  const [searchTerm, setSearchTerm] = useState('')
  const [hoveredBroker, setHoveredBroker] = useState<string | null>(null)
  const [scanned, setScanned] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setScanned(true), 2000)
    return () => clearTimeout(timer)
  }, [])

  const categories = [
    { id: 'major', label: 'Major Indian', count: 8, icon: '⭐' },
    { id: 'full', label: 'Full Variants', count: 20, icon: '🚀' },
    { id: 'mid', label: 'Mid-tier', count: 15, icon: '🌟' },
    { id: 'small', label: 'Regional', count: 39, icon: '🔸' },
    { id: 'integrations', label: 'Integrations', count: 2, icon: '🔗' },
  ]

  const getStatusBadge = (status: string) => {
    const badges = {
      connected: { text: 'Connected', class: 'connected' },
      ready: { text: 'Ready', class: 'ready' },
      available: { text: 'Available', class: 'available' },
    }
    return badges[status as keyof typeof badges] || badges.available
  }

  const currentBrokers = BROKERS[selectedCategory as keyof typeof BROKERS] || []
  const filteredBrokers = searchTerm 
    ? currentBrokers.filter(b => b.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : currentBrokers

  return (
    <div className="brokers-page">
      {/* Header Section */}
      <section className="page-hero">
        <div className="hero-content">
          <span className="ai-badge">✦ Neural Network</span>
          <h1>Broker Connect Hub</h1>
          <p>Connect to 82+ broker adapters with AI-powered optimization</p>
        </div>
        <div className="hero-scanner">
          <div className={`scanner-orb ${scanned ? 'scanned' : ''}`}>
            <div className="orb-ring" />
            <div className="orb-ring ring-2" />
            <div className="orb-core">🔗</div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-number">82</span>
          <span className="stat-label">Total Adapters</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">8</span>
          <span className="stat-label">Connected</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">74</span>
          <span className="stat-label">Ready to Connect</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">5</span>
          <span className="stat-label">Exchanges</span>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="category-tabs">
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
          >
            <span className="tab-icon">{cat.icon}</span>
            <span className="tab-label">{cat.label}</span>
            <span className="tab-count">{cat.count}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="search-container">
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search broker adapters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-btn" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
      </div>

      {/* Broker Grid */}
      <div className="broker-grid">
        {filteredBrokers.length === 0 ? (
          <EmptyState
            title="No Brokers Found"
            description={searchTerm 
              ? `No broker adapters match "${searchTerm}". Try a different search term.`
              : "No broker adapters available in this category."
            }
            illustration="search"
            action={searchTerm ? {
              label: 'Clear Search',
              onClick: () => setSearchTerm(''),
              icon: '🔍'
            } : undefined}
          />
        ) : (
          filteredBrokers.map((broker, index) => {
            const badge = getStatusBadge(broker.status)
            return (
              <div 
                key={broker.id}
                className={`broker-card ${hoveredBroker === broker.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredBroker(broker.id)}
                onMouseLeave={() => setHoveredBroker(null)}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="card-glow" />
                <div className="broker-header">
                  <div className="broker-icon">
                    <BrokerLogo brokerId={broker.id} name={broker.name} size={48} />
                  </div>
                  <div className={`status-dot ${badge.class}`} />
                </div>
                <h3 className="broker-name">{broker.name}</h3>
                {broker.traders !== '-' && (
                  <span className="broker-traders">{broker.traders} traders</span>
                )}
                <div className="broker-footer">
                  <span className={`status-badge ${badge.class}`}>
                    {badge.text}
                  </span>
                  <button className="connect-btn">
                    {broker.status === 'connected' ? 'Manage' : 'Connect'}
                  </button>
                </div>
                <div className="card-shine" />
              </div>
            )
          })
        )}
      </div>

      {/* Network Visualization */}
      <section className="network-section card">
        <div className="section-header">
          <h2 className="section-title">🔮 Broker Network Status</h2>
          <span className="live-badge">
            <span className="live-dot" />
            LIVE
          </span>
        </div>
        <div className="network-grid">
          <div className="network-node major">
            <div className="node-icon">⭐</div>
            <span className="node-label">Major</span>
            <span className="node-value">8</span>
          </div>
          <div className="network-connector" />
          <div className="network-node full">
            <div className="node-icon">🚀</div>
            <span className="node-label">Full</span>
            <span className="node-value">20</span>
          </div>
          <div className="network-connector" />
          <div className="network-node mid">
            <div className="node-icon">🌟</div>
            <span className="node-label">Mid</span>
            <span className="node-value">15</span>
          </div>
          <div className="network-connector" />
          <div className="network-node small">
            <div className="node-icon">🔸</div>
            <span className="node-label">Regional</span>
            <span className="node-value">39</span>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <AnimateOnScroll animation="fadeInUp">
        <section className="integrations-section">
          <IntegrationsList />
        </section>
      </AnimateOnScroll>

      {/* API Key Manager */}
      <AnimateOnScroll animation="fadeInUp" delay={100}>
        <section className="api-keys-section">
          <APIKeyManager />
        </section>
      </AnimateOnScroll>
    </div>
  )
}

export default Brokers